import { type GetServerSideProps, type NextPage } from "next";
import superjson from "superjson";
import { DashboardUnavailable } from "~/components/dashboard/DashboardUnavailable";
import type { DashboardInitialData } from "~/server/dashboard/getDashboardInitialData";
import { loadAuthenticatedDashboard } from "~/server/dashboard/loadAuthenticatedDashboard";
import HeadComponent from "~/ui/head";
import getUser from "~/utils/getUser";
import { type PageProps } from "~/utils/types";
import dynamic from "next/dynamic";

// The signed-out page renders the sign-in panel without loading member UI
// or its tRPC and react-query providers. Both remain server rendered when
// the authenticated page needs them.
const DashboardV4 = dynamic(
  () => import("~/components/dashboard/DashboardV4")
);
const ApiProviders = dynamic(() =>
  import("~/components/app/ApiProviders").then((mod) => mod.ApiProviders)
);

// The Clerk sign-in panel loads as its own dynamically imported chunk with
// its ClerkProvider inside (TwtSignInPanelWithProvider). A static import
// would put the @clerk/nextjs client SDK into the budgeted /dashboard route
// chunk (scripts/oracle2/bundle-budget.mjs, home target); before that it sat
// in the shared _app chunk and failed the shell budget. next/dynamic keeps
// server rendering for the login wall and moves the SDK into a chunk only
// signed-out visitors load.
const TwtSignInPanel = dynamic(
  () =>
    import("~/components/auth/TwtSignInPanel").then(
      (mod) => mod.TwtSignInPanelWithProvider
    ),
  { loading: () => null }
);

// The one-time feature showcase overlays the workspace after setup completes.
// It loads its own onboarding state and renders nothing when the tour was
// already acknowledged, so it stays out of the dashboard's data path.
const FeatureTourGate = dynamic(
  () =>
    import("~/components/onboarding/FeatureTour").then(
      (mod) => mod.FeatureTourGate
    ),
  { ssr: false }
);
import {
  clearPendingWhopEmailCookie,
  clearPendingWhopFlowCookie,
} from "~/server/whopLoginCookies";
import { appendSetCookies } from "~/utils/getUserFromCtx";
import { TwtBrandWord } from "~/components/TwtBrand";
import { WebAppHead } from "~/components/dashboard/alerts/WebAppHead";
import { SignInModalStyles } from "~/components/auth/SignInModalStyles";
import { wallSerif as releaseSerif } from "~/styles/fonts/signInWall";
import { endMemberSessions } from "~/server/session/endMemberSessions";
import { APP_SESSION_COOKIE_NAME } from "~/utils/signToken";
import { safeRedirectPath } from "~/utils/safeRedirectPath";
import {
  AUTHENTICATION_UNAVAILABLE_RETRY_AFTER_MS,
  isAuthenticationUnavailableError,
} from "~/utils/authenticationUnavailable";
import {
  CHECKOUT_ACCOUNT_MISMATCH_COOKIE_NAME,
  checkoutAccountIntentMatchesDisplay,
  clearCheckoutAccountMismatchCookie,
  openCheckoutAccountMismatchIntent,
} from "~/utils/checkoutAccountMismatch";
import {
  DASHBOARD_EMAIL_FALLBACK_CODE,
  dashboardEmailFallbackMessage,
} from "~/utils/dashboardEmailFallbackUrl";
import { checkoutAccountMismatchIntentHasState } from "~/server/session/checkoutAccountMismatchStore";
import { clerkConfigured } from "~/server/auth/clerkConfig";
import { financeHomeEnabled } from "~/server/financeHome/config";
import { releaseApprovedFor } from "~/lib/leviathanGateAdmin";
import {
  finishFinanceHome,
  hasAlertsAccess,
  startFinanceHomeMember,
  startFinanceHomeShared,
} from "~/server/financeHome/load";
import { phoneRequest } from "~/server/financeHome/firstScreenEdge";
import { deferBelowPhoneFold } from "~/lib/financeHome/types";
import { grantWatchlistListPass } from "~/server/financePages/watchlistListGrant";
import {
  clerkSkippedFor,
  dropClerkHeaders,
  fullAuthUrl,
} from "~/server/financePages/financeAuth";
import {
  grantFirstScreenPass,
  revokeFirstScreenPass,
} from "~/server/financeHome/firstScreenSession";

// The finance home renders only where its switch is on (src/server/financeHome/switch.ts),
// so its code loads as its own chunk and the Overview route is unchanged elsewhere.
const FinanceHome = dynamic(
  () => import("~/components/finance-home/FinanceHome")
);

const pageTitle = "TWT | Dashboard Home";

export const getServerSideProps: GetServerSideProps = async (context) => {
  if (!(await clerkSkippedFor(context))) return dashboardProps(context);
  dropClerkHeaders(context.req.headers);
  const result = await dashboardProps(context);
  if ("props" in result && "financeHomeJson" in result.props) {
    const timing = context.res.getHeader("Server-Timing");
    context.res.setHeader("Server-Timing", [timing, "clerk-skipped;dur=0"].filter(Boolean).join(", "));
    return result;
  }
  // Anything but the member's finance home needs Clerk (signing in, restoring or revoking its session), so the
  // browser asks again through it and this response keeps none of the cookies the check set.
  context.res.removeHeader("Set-Cookie");
  context.res.removeHeader("Retry-After");
  context.res.statusCode = 200;
  return { redirect: { destination: fullAuthUrl(context.resolvedUrl), permanent: false } };
};

const dashboardProps: GetServerSideProps = async (context) => {
  const requestStart = performance.now();
  let user;
  try {
    user = await getUser({ context });
  } catch (error) {
    if (!isAuthenticationUnavailableError(error)) throw error;

    context.res.statusCode = 503;
    context.res.setHeader(
      "Retry-After",
      String(AUTHENTICATION_UNAVAILABLE_RETRY_AFTER_MS / 1000)
    );
    return {
      props: {
        user: null,
        dashboardUnavailable: true,
        dashboardUnavailableReason: "authentication",
        retryAfterMs: AUTHENTICATION_UNAVAILABLE_RETRY_AFTER_MS,
        initialDashboardDataJson: null,
      },
    };
  }
  const requestedLoginNext = safeRedirectPath(context.query.next, "/dashboard");
  const openEmailField =
    context.query.login === "email-code" ||
    context.query.login === "verify-whop";
  const loginErrorCode = context.query.login_error_code;
  const rejectLoginErrorQuery =
    context.query.login_error !== undefined ||
    (loginErrorCode !== undefined &&
      loginErrorCode !== DASHBOARD_EMAIL_FALLBACK_CODE);
  if (rejectLoginErrorQuery) {
    const params = new URLSearchParams({
      login: "email-code",
      next: requestedLoginNext,
    });
    if (loginErrorCode === DASHBOARD_EMAIL_FALLBACK_CODE) {
      params.set("login_error_code", DASHBOARD_EMAIL_FALLBACK_CODE);
    }
    return {
      redirect: {
        destination: `/dashboard?${params.toString()}`,
        permanent: false,
      },
    };
  }
  const initialLoginError = openEmailField
    ? dashboardEmailFallbackMessage(loginErrorCode)
    : "";

  if (
    openEmailField ||
    context.req.cookies.twt_pending_whop_flow ||
    context.req.cookies.twt_pending_whop_email
  ) {
    appendSetCookies(context.res, [
      clearPendingWhopFlowCookie(),
      clearPendingWhopEmailCookie(),
    ]);
  }

  if (user && context.query.checkout_account_mismatch === "1") {
    let mismatchIntent: Awaited<
      ReturnType<typeof openCheckoutAccountMismatchIntent>
    >;
    let mismatchIntentIsPending = false;
    try {
      mismatchIntent = await openCheckoutAccountMismatchIntent(
        context.req.cookies[CHECKOUT_ACCOUNT_MISMATCH_COOKIE_NAME]
      );
      mismatchIntentIsPending = Boolean(
        mismatchIntent &&
          checkoutAccountIntentMatchesDisplay({
            intent: mismatchIntent,
            currentUserId: user.id,
            currentAppSession: context.req.cookies[APP_SESSION_COOKIE_NAME],
          }) &&
          (await checkoutAccountMismatchIntentHasState(
            mismatchIntent,
            "pending"
          ))
      );
    } catch (error) {
      if (!isAuthenticationUnavailableError(error)) throw error;
      context.res.statusCode = 503;
      context.res.setHeader(
        "Retry-After",
        String(AUTHENTICATION_UNAVAILABLE_RETRY_AFTER_MS / 1000)
      );
      return {
        props: {
          user: null,
          dashboardUnavailable: true,
          dashboardUnavailableReason: "authentication",
          retryAfterMs: AUTHENTICATION_UNAVAILABLE_RETRY_AFTER_MS,
          initialDashboardDataJson: null,
        },
      };
    }

    if (mismatchIntent && mismatchIntentIsPending) {
      return {
        props: {
          user,
          checkoutAccountMismatch: {
            currentName: mismatchIntent.currentName,
            currentEmail: mismatchIntent.currentEmail,
            checkoutName: mismatchIntent.checkoutName,
            checkoutEmail: mismatchIntent.checkoutEmail,
          },
          initialDashboardDataJson: null,
        },
      };
    }

    appendSetCookies(context.res, [clearCheckoutAccountMismatchCookie()]);
  }

  const loginNext = requestedLoginNext;
  const notMember = context.query.notmember === "1";

  if (openEmailField) {
    return {
      props: {
        user,
        loginNext,
        openEmailField: true,
        initialLoginError,
        clerkEnabled: clerkConfigured(),
        notMember,
        initialDashboardDataJson: null,
      },
    };
  }

  if (user) {
    // Market data that is the same for every member, and the member's own
    // Watchlist and setup reads, start beside the dashboard load.
    const financeNow = Date.now();
    const authDone = performance.now();
    const financeShared = financeHomeEnabled(releaseApprovedFor(context.req))
      ? startFinanceHomeShared(financeNow)
      : null;
    const financeMember = financeShared
      ? startFinanceHomeMember(user.id, financeNow)
      : null;
    const loaded = await loadAuthenticatedDashboard({
      user,
      requestId: crypto.randomUUID(),
    });

    if (loaded.state === "transient") {
      context.res.statusCode = 503;
      context.res.setHeader(
        "Retry-After",
        String(Math.ceil(loaded.retryAfterMs / 1000))
      );
      return {
        props: {
          user,
          loginNext,
          notMember,
          dashboardUnavailable: true,
          retryAfterMs: loaded.retryAfterMs,
        },
      };
    }

    if (loaded.state === "agreements_unavailable") {
      context.res.statusCode = 503;
      context.res.setHeader(
        "Retry-After",
        String(Math.ceil(loaded.retryAfterMs / 1000))
      );
      return {
        props: {
          user,
          dashboardUnavailable: true,
          dashboardUnavailableReason: "agreements",
          retryAfterMs: loaded.retryAfterMs,
          initialDashboardDataJson: null,
        },
      };
    }

    if (loaded.state === "agreements_required") {
      // Agreements enforcement (dark flag) refused to treat this member as
      // fully onboarded: send them into the signing step. The re-entry link
      // re-verifies payment server-side before showing or recording anything.
      return {
        redirect: {
          destination: loaded.reentryPath,
          permanent: false,
        },
      };
    }

    if (loaded.state === "signed_out") {
      await endMemberSessions({
        userId: user.id,
        reason: "w_bundle_invalid",
        res: context.res,
      });
      appendSetCookies(context.res, [
        clearPendingWhopFlowCookie(),
        clearPendingWhopEmailCookie(),
      ]);
      await revokeFirstScreenPass({
        req: context.req,
        res: context.res,
        quoteUser: null,
      });
      user = null;
    } else {
      if (
        financeShared &&
        financeMember &&
        !loaded.data.membershipSetupPending
      ) {
        const dashboardDone = performance.now();
        const financeHome = await finishFinanceHome({
          user: loaded.user,
          dashboard: loaded.data,
          shared: financeShared,
          member: financeMember,
          now: financeNow,
        });
        await grantFirstScreenPass({
          req: context.req,
          res: context.res,
          memberId: loaded.user.id,
          quoteUser: loaded.user,
        });
        if (financeHome.watchlist.state === "ready")
          await grantWatchlistListPass({
            req: context.req,
            res: context.res,
            memberId: loaded.user.id,
            list: {
              symbols: financeHome.watchlist.data.symbols,
              revision: financeHome.watchlist.data.revision,
              hasLiveAccess: hasAlertsAccess(loaded.user, loaded.data),
            },
          });
        context.res.setHeader(
          "Server-Timing",
          [
            `auth;dur=${Math.round(authDone - requestStart)}`,
            `dashboard;dur=${Math.round(dashboardDone - authDone)}`,
            `finance;dur=${Math.round(performance.now() - dashboardDone)}`,
          ].join(", ")
        );
        return {
          props: {
            user: loaded.user,
            loginNext,
            notMember,
            financeHomeJson: JSON.stringify(
              phoneRequest({
                get: (name) => {
                  const value = context.req.headers[name];
                  return (Array.isArray(value) ? value[0] : value) ?? null;
                },
              })
                ? deferBelowPhoneFold(financeHome)
                : financeHome
            ),
          },
        };
      }
      await revokeFirstScreenPass({
        req: context.req,
        res: context.res,
        quoteUser: loaded.user,
      });
      return {
        props: {
          user: loaded.user,
          loginNext,
          notMember,
          initialDashboardDataJson: superjson.stringify(loaded.data),
        },
      };
    }
  }

  return {
    props: {
      user,
      loginNext,
      openEmailField,
      initialLoginError,
      clerkEnabled: clerkConfigured(),
      notMember,
      initialDashboardDataJson: null,
    },
  };
};

type DashboardPageProps = PageProps & {
  loginNext?: string;
  openEmailField?: boolean;
  initialLoginError?: string;
  /** Server-decided: both Clerk keys are configured on this deployment. */
  clerkEnabled?: boolean;
  notMember?: boolean;
  initialDashboardDataJson?: string | null;
  financeHomeJson?: string | null;
  dashboardUnavailable?: boolean;
  dashboardUnavailableReason?: "authentication" | "agreements";
  retryAfterMs?: number;
  checkoutAccountMismatch?: {
    currentName: string;
    currentEmail: string;
    checkoutName: string;
    checkoutEmail: string;
  };
};

// The signed-out wall reads in the Leviathan type setting: Source Serif 4 for
// the heading, the device's system font for the interface. The serif variable
// is set on the wall's <main> and read by the modal's styles
// (SignInModalStyles.tsx). The sans variable is set in globals.css.
const wallFontClass = releaseSerif.variable;

const Page: NextPage<DashboardPageProps> = ({
  renderingPage,
  user,
  loginNext = "/dashboard",
  openEmailField = false,
  initialLoginError = "",
  clerkEnabled = false,
  notMember = false,
  initialDashboardDataJson = null,
  financeHomeJson = null,
  dashboardUnavailable = false,
  dashboardUnavailableReason,
  retryAfterMs,
  checkoutAccountMismatch,
}) => {
  // A valid session user counts as signed in by their id. A brand-new
  // email-only account has no name and no username yet, and inferring login
  // from those fields showed such a member the sign-in wall here while
  // /account and /onboarding rendered signed in.
  const isLoggedIn = Boolean(user?.id);
  const initialDashboardData = initialDashboardDataJson
    ? superjson.parse<DashboardInitialData>(initialDashboardDataJson)
    : null;

  if (dashboardUnavailable) {
    return (
      <>
        <HeadComponent title={pageTitle} />
        <DashboardUnavailable
          retryAfterMs={retryAfterMs}
          reason={dashboardUnavailableReason}
        />
      </>
    );
  }

  if (!openEmailField && isLoggedIn && checkoutAccountMismatch) {
    return (
      <>
        <HeadComponent title={pageTitle} />
        <main className={`whop-login-page ${wallFontClass}`}>
          <SignInModalStyles />
          <section className="whop-login-stage">
            <div
              className="whop-login-modal twt-signin-modal"
              role="region"
              aria-labelledby="checkout-account-title"
            >
              <div className="whop-login-modal-head">
                <div className="whop-login-icons" aria-hidden="true">
                  <span className="whop-login-twt-mark">
                    <TwtBrandWord className="text-headline-lg text-[#f2f2ef]" />
                  </span>
                </div>
                <p id="checkout-account-title" className="whop-login-title">
                  Checkout account does not match this dashboard
                </p>
              </div>

              <dl className="whop-login-account-compare">
                <div>
                  <dt>Current account</dt>
                  <dd>
                    {checkoutAccountMismatch.currentName}
                    {checkoutAccountMismatch.currentEmail
                      ? ` · ${checkoutAccountMismatch.currentEmail}`
                      : ""}
                  </dd>
                </div>
                <div>
                  <dt>Checkout account</dt>
                  <dd>
                    {checkoutAccountMismatch.checkoutName}
                    {checkoutAccountMismatch.checkoutEmail
                      ? ` · ${checkoutAccountMismatch.checkoutEmail}`
                      : ""}
                  </dd>
                </div>
              </dl>

              <div className="whop-oauth-actions">
                <form
                  method="post"
                  action="/api/login/checkout-account"
                  style={{ display: "contents" }}
                >
                  <input type="hidden" name="choice" value="current" />
                  <button className="whop-oauth-cancel" type="submit">
                    Continue with current account
                  </button>
                </form>
                <form
                  method="post"
                  action="/api/login/checkout-account"
                  style={{ display: "contents" }}
                >
                  <input type="hidden" name="choice" value="switch" />
                  <button className="whop-oauth-approve" type="submit">
                    Sign out and switch account
                  </button>
                </form>
              </div>
            </div>
          </section>
        </main>
      </>
    );
  }

  if (openEmailField || !isLoggedIn) {
    return (
      <>
        <HeadComponent title={pageTitle} />
        <WebAppHead showNotificationSetup={false} />
        <main className={`whop-login-page ${wallFontClass}`}>
          <SignInModalStyles />
          <section className="whop-login-stage">
            <TwtSignInPanel
              nextPath={loginNext}
              clerkEnabled={clerkEnabled}
              initialError={
                initialLoginError ||
                (notMember
                  ? "This email isn't associated with a TWT membership. Use the email you signed up with."
                  : "")
              }
            />
          </section>
        </main>
      </>
    );
  }

  if (user && financeHomeJson) {
    return (
      <ApiProviders>
        <HeadComponent title={pageTitle} />
        <WebAppHead />
        <FinanceHome user={user} data={JSON.parse(financeHomeJson)} />
        <FeatureTourGate memberId={user.id} />
      </ApiProviders>
    );
  }

  return (
    <ApiProviders>
      <HeadComponent title={pageTitle} />
      <WebAppHead />
      {user ? (
        <>
          <DashboardV4 user={user} initialData={initialDashboardData} />
          <FeatureTourGate memberId={user.id} />
        </>
      ) : null}
    </ApiProviders>
  );
};

export default Page;
// export const runtime = "experimental-edge";
